import React from 'react';
import { useState } from 'react';
import { withStyles } from '@material-ui/core/styles';
import classNames from 'classnames';

import AppBar from '@material-ui/core/AppBar';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Typography from '@material-ui/core/Typography';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Toolbar from '@material-ui/core/Toolbar';
import GroupInfoTab from './forms/GroupInfoTab';
import EditableLabel from './forms/EditableLabel';
import SharesTab from './forms/SharesTab';
import DefaultDialog from 'ui/dialog/DefaultDialog';
import DialogButtons from 'ui/dialog/Buttons';

import formatters from '../../../formatters';
import DualListBox from '../../../ui/duallist/DualListBox';

const groupFormStyles = theme => ({
    appBar: {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.secondary.main,
        paddingBottom: '35px',
    },
    appBarRoot: {
        position: 'relative',
        '&:after': {
            content: '""',
            borderBottom: `3px solid ${theme.palette.grey['300']}`,
            position: 'absolute',
            bottom: 43,
            left: -24,
            right: -24,
            zIndex: -1,
        },
    },
    dialogToolbar: { padding: 0 },
    dialogToolbarTabs: { flex: 1 },
    list: { height: 450, marginBottom: -45 },
    listAdd: { height: 400, marginBottom: -45 },
});

const MAX_GROUP_NAME_LENGTH = 500;

const groupAlreadyExist = (groups, groupInfo) => {
    return Boolean(
        groups.find(
            group =>
                group.name === groupInfo.name && group.uuid !== groupInfo.uuid
        )
    );
};

const validate = (groupInfo, groups) => {
    const trimmedName = (groupInfo.name || '').trim();

    const errors = {
        name: trimmedName ? '' : 'Required',
    };

    if (!errors.name) {
        if (trimmedName.length > MAX_GROUP_NAME_LENGTH) {
            errors.name = `Invalid: value must not exceed ${MAX_GROUP_NAME_LENGTH} characters`;
        } else if (groupAlreadyExist(groups, groupInfo)) {
            errors.name = 'Please enter a unique group name.';
        }
    }

    const valid = Object.values(errors).reduce((acc, val) => {
        if (acc) {
            return !Boolean(val);
        }
        return acc;
    }, true);

    return [valid, errors];
};

const renderGroupDialogTitle = (
    groupInfo,
    groupInfoErrors,
    setGroupInfo,
    managedByPb,
    title,
    mode
) => {
    if (mode === 'edit') {
        return (
            <EditableLabel
                managedByPb={managedByPb}
                groupInfo={groupInfo}
                groupInfoErrors={groupInfoErrors}
                onChange={(prop, val) => {
                    setGroupInfo({ ...groupInfo, [prop]: val });
                }}
            />
        );
    }
    return title;
};

const getInitialGroupState = currentGroup => {
    if (currentGroup) {
        return {
            uuid: currentGroup.uuid,
            name: currentGroup.name,
            disabled: !currentGroup.enabled,
        };
    }

    return {
        name: '',
    };
};

const getInitialUsers = currentGroup => {
    if (currentGroup) {
        return currentGroup.users.map(({ uuid }) => uuid);
    }
};

const leftAction = (onDelete, managedByPb, currentGroup) => {
    if (currentGroup) {
        if (onDelete && managedByPb) {
            return <DialogButtons.Primary onClick={onDelete} label="Delete" />;
        } else if (!managedByPb) {
            return (
                <Typography style={{ paddingLeft: 15 }} color="secondary">
                    Managed By:{' '}
                    {formatters.managedByLabel(currentGroup.managed_by)}
                </Typography>
            );
        }
        return (
            <Typography style={{ paddingLeft: 15 }} color="secondary">
                Managed By: {formatters.managedByLabel(currentGroup.managed_by)}
            </Typography>
        );
    }
};

const isUserSelectable = (user, group) => {
    if (group) {
        if (group.population_mode === 'automatic') {
            return false;
        }
        if (group.managed_by === 'PB') {
            return true;
        }
    }
    return user.managed_by === 'PB' || user.managed_by === 'Active Directory';
};

const getUserOptions = (users, currentGroup) => {
    if (users && users.length) {
        return users
            .filter(user => user.item_type === 'account')
            .map(user => ({
                value: user.uuid,
                selectable: isUserSelectable(user, currentGroup),
                label: formatters.displayName(user.email, user.managed_by),
            }));
    }
    if (currentGroup) {
        return currentGroup.users.map(user => ({
            value: user.uuid,
            label: user.email,
        }));
    }
    return [];
};

let GroupForm = props => {
    const {
        classes,
        open,
        onOk,
        onCancel,
        cancelButtonName = 'Cancel',
        okButtonName = 'Add Group',
        onDelete,
        formErrors,
        mode,
        currentGroup,
        groups,
    } = props;

    const managedByPb = currentGroup ? currentGroup.managed_by === 'PB' : true;
    const title = currentGroup ? `${currentGroup.name}` : 'Add Group';

    const usersOptions = getUserOptions(props.users, currentGroup);

    const [tab, setTab] = useState(0);
    const [users, setUsers] = useState(getInitialUsers(currentGroup));

    const [groupInfo, setGroupInfo] = useState(
        getInitialGroupState(currentGroup)
    );

    const [groupInfoErrors, setGroupInfoErrors] = useState({
        name: '',
    });

    return (
        <DefaultDialog open={open} onClose={onCancel} autoHeight={false}>
            <DialogTitle>
                {renderGroupDialogTitle(
                    groupInfo,
                    groupInfoErrors,
                    setGroupInfo,
                    managedByPb,
                    title,
                    mode
                )}
            </DialogTitle>
            <DialogContent>
                {mode === 'new' && (
                    <GroupInfoTab
                        mode={mode}
                        managedByPb={managedByPb}
                        groupInfo={groupInfo}
                        groupInfoErrors={groupInfoErrors}
                        onChange={(prop, val) => {
                            setGroupInfo({ ...groupInfo, [prop]: val });
                        }}
                    />
                )}
                {formErrors && (
                    <Typography color="error">{formErrors}</Typography>
                )}
                <AppBar
                    position="static"
                    className={classes.appBar}
                    elevation={0}
                    classes={{
                        root: classes.appBarRoot,
                    }}
                >
                    <Toolbar className={classes.dialogToolbar}>
                        <Tabs
                            value={tab}
                            onChange={(event, val) => setTab(val)}
                            textColor="secondary"
                            className={classes.dialogToolbarTabs}
                        >
                            <Tab label="Group Members" />
                            {mode !== 'new' && <Tab label="Shares" />}
                        </Tabs>
                    </Toolbar>
                </AppBar>
                {tab === 0 && (
                    <DualListBox
                        disabled={mode === 'details'}
                        options={usersOptions}
                        initialValue={users}
                        sourceListHeaderName="Users"
                        destinationListHeaderName="Group Members"
                        onChange={values => setUsers(values)}
                        className={classNames({
                            [classes.list]: mode !== 'new',
                            [classes.listAdd]: mode === 'new',
                        })}
                    />
                )}
                {tab === 1 && mode !== 'new' && (
                    <SharesTab shares={currentGroup.group_share_batches} />
                )}
            </DialogContent>
            <DialogActions>
                <DialogButtons withLeft>
                    <div>
                        {tab === 0 &&
                            leftAction(onDelete, managedByPb, currentGroup)}
                    </div>
                    <div>
                        {onCancel && (
                            <DialogButtons.Secondary
                                label={cancelButtonName}
                                onClick={onCancel}
                            />
                        )}
                        <DialogButtons.Primary
                            variant="contained"
                            label={okButtonName}
                            onClick={() => {
                                const [valid, errors] = validate(
                                    groupInfo,
                                    groups
                                );
                                setGroupInfoErrors(errors);
                                if (valid) {
                                    groupInfo.users = users;
                                    onOk(groupInfo);
                                }
                            }}
                            autoFocus
                        />
                    </div>
                </DialogButtons>
            </DialogActions>
        </DefaultDialog>
    );
};

export default withStyles(groupFormStyles)(GroupForm);
